/**
 * Sprint T-15 · Founder Security Center · Compliance / GDPR tab.
 *
 * Consumes ONLY /api/security/compliance/*.
 *
 * - Policies: read-only retention table
 * - Export "me": dump full personal data as JSON download
 * - Superadmin tools: export and soft/hard delete of any user/customer
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, FileText, ShieldAlert, BookOpenText } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge, StatusBadge } from "../advisor";
import {
  useCompliancePolicies, useExportMe, useExportOther,
  useDeleteMe, useDeleteUser, useDeleteCustomer,
} from "../../hooks/useSecurity";

function downloadJson(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function PoliciesCard() {
  const q = useCompliancePolicies();
  return (
    <WidgetCard
      title="Políticas de retención"
      description="Tabla declarativa · usada por las purgas automáticas de cada colección."
      citations={["/api/security/compliance/policies"]}
      testId="widget-compliance-policies"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar la tabla de políticas." />}
      {q.data && (
        <div className="overflow-x-auto" data-testid="compliance-policies-table-wrap">
          <table className="min-w-full text-xs" data-testid="compliance-policies-table">
            <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
              <tr>
                <th className="px-2 py-2 text-left">Colección</th>
                <th className="px-2 py-2 text-left">Retención</th>
                <th className="px-2 py-2 text-left">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(q.data.policies || []).map((p) => (
                <tr key={p.collection} data-testid={`policy-row-${p.collection}`} className="border-b border-[var(--ag-border)]/60">
                  <td className="px-2 py-1.5 align-top font-mono">{p.collection}</td>
                  <td className="px-2 py-1.5 align-top">
                    {p.retention_days != null ? (
                      <StatusBadge status="medio" label={`${p.retention_days} días`} testId={`policy-retention-${p.collection}`} />
                    ) : (
                      <StatusBadge status="indeterminado" label="indefinido" testId={`policy-retention-${p.collection}`} />
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top text-[var(--ag-text-dim)]">{p.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WidgetCard>
  );
}

function ExportMeCard() {
  const m = useExportMe();
  const [counts, setCounts] = useState(null);

  const onExport = async () => {
    try {
      const data = await m.mutateAsync();
      setCounts(data.counts || null);
      const filename = `autoglow-export-${new Date().toISOString().slice(0, 10)}.json`;
      downloadJson(filename, data);
      toast.success("Export descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando export");
    }
  };

  return (
    <WidgetCard
      title="Mi export RGPD"
      description="Descarga JSON con todos tus datos personales · audit log queda registrado."
      citations={["/api/security/compliance/export/me", "audit.gdpr.export"]}
      testId="widget-compliance-export-me"
    >
      <button
        data-testid="compliance-export-me-btn"
        type="button"
        onClick={onExport}
        disabled={m.isPending}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        <Download className="h-3 w-3" />
        {m.isPending ? "Generando…" : "Descargar mis datos (JSON)"}
      </button>
      {counts && (
        <div className="mt-3 grid grid-cols-2 gap-1 md:grid-cols-3" data-testid="compliance-export-counts">
          {Object.entries(counts).map(([k, v]) => (
            <div key={k} className="rounded border border-[var(--ag-border)] px-2 py-1 text-[10px]">
              <span className="text-[var(--ag-text-dim)]">{k}</span>{" "}
              <span className="font-mono">{v}</span>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

function DeleteMeCard() {
  const m = useDeleteMe();
  const [confirm, setConfirm] = useState("");

  const onDelete = async () => {
    if (confirm !== "BORRAR MI CUENTA") return;
    try {
      const res = await m.mutateAsync();
      toast.success(`Cuenta anonimizada · ${res.actions?.length || 0} acciones registradas`);
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando cuenta");
    }
  };

  return (
    <WidgetCard
      title="Eliminar mi cuenta (soft delete)"
      description="Anonimiza tu cuenta · conserva trazabilidad fiscal · no se puede revertir."
      citations={["/api/security/compliance/me", "audit.gdpr.delete"]}
      testId="widget-compliance-delete-me"
    >
      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-600">
        <ShieldAlert className="mr-1 inline h-3 w-3" />
        Para evitar accidentes, escribe exactamente <code className="font-mono">BORRAR MI CUENTA</code> antes de confirmar.
        Superadmin no puede auto-eliminarse.
      </div>
      <div className="mt-2 flex gap-2">
        <input
          data-testid="compliance-delete-me-input"
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="BORRAR MI CUENTA"
          className="flex-1 rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
        />
        <button
          data-testid="compliance-delete-me-btn"
          type="button"
          onClick={onDelete}
          disabled={confirm !== "BORRAR MI CUENTA" || m.isPending}
          className="inline-flex items-center gap-1 rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50"
        >
          <Trash2 className="h-3 w-3" />
          {m.isPending ? "Eliminando…" : "Eliminar mi cuenta"}
        </button>
      </div>
    </WidgetCard>
  );
}

function AdminExportCard() {
  const m = useExportOther();
  const [userId, setUserId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");

  const onExport = async () => {
    if (!userId && !customerId && !email) {
      toast.error("Especifica al menos uno: user_id, customer_id o email.");
      return;
    }
    try {
      const data = await m.mutateAsync({
        user_id: userId || undefined,
        customer_id: customerId || undefined,
        email: email || undefined,
      });
      const stem = userId || customerId || email || "subject";
      const filename = `autoglow-export-${stem.replace(/[^a-z0-9-]/gi, "_").slice(0, 32)}-${new Date().toISOString().slice(0, 10)}.json`;
      downloadJson(filename, data);
      toast.success(`Export generado · ${Object.values(data.counts || {}).reduce((a, b) => a + b, 0)} registros`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando export");
    }
  };

  return (
    <WidgetCard
      title="Superadmin · Export de otro sujeto"
      description="Por user_id, customer_id o email. Audit log queda registrado con metadata."
      citations={["/api/security/compliance/export", "audit.gdpr.export"]}
      testId="widget-compliance-export-other"
    >
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <input
          data-testid="compliance-export-userid"
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="user_id"
          className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
        />
        <input
          data-testid="compliance-export-customerid"
          type="text"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          placeholder="customer_id"
          className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
        />
        <input
          data-testid="compliance-export-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email"
          className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
        />
      </div>
      <button
        data-testid="compliance-export-other-btn"
        type="button"
        onClick={onExport}
        disabled={m.isPending}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)] disabled:opacity-50"
      >
        <FileText className="h-3 w-3" />
        {m.isPending ? "Generando…" : "Generar export"}
      </button>
    </WidgetCard>
  );
}

function AdminDeleteCard() {
  const dUser = useDeleteUser();
  const dCust = useDeleteCustomer();
  const [type, setType] = useState("user");
  const [targetId, setTargetId] = useState("");
  const [mode, setMode] = useState("soft");
  const [confirm, setConfirm] = useState("");

  const onDelete = async () => {
    if (!targetId.trim()) {
      toast.error("ID obligatorio.");
      return;
    }
    if (confirm !== "CONFIRMAR") {
      toast.error("Escribe CONFIRMAR para proceder.");
      return;
    }
    try {
      const res = type === "user"
        ? await dUser.mutateAsync({ user_id: targetId.trim(), mode })
        : await dCust.mutateAsync({ customer_id: targetId.trim(), mode });
      toast.success(`OK · ${(res.actions || []).length} acciones · mode=${mode}`);
      setTargetId("");
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const pending = dUser.isPending || dCust.isPending;

  return (
    <WidgetCard
      title="Superadmin · Derecho al olvido"
      description="Soft (anonimizar · mantiene fiscal) o hard (borrado completo · solo si no hay restricciones AEAT)."
      citations={["/api/security/compliance/user/{id}", "/api/security/compliance/customer/{id}"]}
      testId="widget-compliance-delete-other"
    >
      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        <div className="md:col-span-1">
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Tipo</label>
          <select
            data-testid="compliance-delete-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
          >
            <option value="user">user</option>
            <option value="customer">customer</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">ID</label>
          <input
            data-testid="compliance-delete-id"
            type="text"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            placeholder="uuid del sujeto"
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
          />
        </div>
        <div className="md:col-span-1">
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Modo</label>
          <select
            data-testid="compliance-delete-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
          >
            <option value="soft">soft · anonimizar</option>
            <option value="hard">hard · borrar</option>
          </select>
        </div>
      </div>
      <div className="mt-2 flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <input
          data-testid="compliance-delete-confirm"
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Escribe "CONFIRMAR"'
          className="flex-1 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-xs"
        />
        <button
          data-testid="compliance-delete-other-btn"
          type="button"
          onClick={onDelete}
          disabled={pending || confirm !== "CONFIRMAR"}
          className="inline-flex items-center gap-1 rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50"
        >
          <Trash2 className="h-3 w-3" />
          {pending ? "Procesando…" : `Ejecutar (${mode})`}
        </button>
      </div>
    </WidgetCard>
  );
}

export default function ComplianceTab() {
  return (
    <section className="space-y-4" data-testid="security-compliance-tab">
      <WidgetCard
        title="Compliance · RGPD"
        description="Infraestructura legal · export portable, derecho al olvido, retention policies."
        citations={["/api/security/compliance/*"]}
        testId="widget-compliance-intro"
      >
        <div className="flex items-center gap-2 text-xs text-[var(--ag-text-dim)]">
          <BookOpenText className="h-4 w-4" />
          Los workflows contractuales (DPA, subprocesadores) viven fuera de la app · este panel cubre las obligaciones técnicas.
        </div>
      </WidgetCard>

      <PoliciesCard />

      <WidgetGrid cols="lg:grid-cols-2">
        <ExportMeCard />
        <DeleteMeCard />
      </WidgetGrid>

      <WidgetCard
        title="Operaciones superadmin"
        description="Visible para todos los founders · backend rechaza con 403 si tu rol no es superadmin."
        citations={["security.access_control.require_superadmin"]}
        testId="widget-compliance-admin-zone"
      >
        <div className="space-y-3" data-testid="compliance-admin-zone">
          <AdminExportCard />
          <AdminDeleteCard />
        </div>
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["RGPD art. 15 + 17", "AEAT 5 años fiscal", "audit.gdpr.* trail"]} />
      </footer>
    </section>
  );
}
